import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ShoppingCartIcon,
  PlusIcon,
  MinusIcon,
  FunnelIcon,
  XMarkIcon,
  SparklesIcon,
  TagIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import CompanySelector from '../../components/agent/CompanySelector';
import useAgentDraftCart from '../../hooks/useAgentDraftCart';
// CartSidebar removed - cart now opens as a separate page (LUM-15)
import BundleCard from '../../components/agent/BundleCard';
import BundleDetailModal from '../../components/agent/BundleDetailModal';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';
import FormInput from '../../components/ui/FormInput';
import AccessibleButton from '../../components/ui/AccessibleButton';
import ImageLightbox from '../../components/ui/ImageLightbox';

/**
 * AgentProductsPage - Mobile First Product Catalog
 * סוכן שטח - קטלוג מוצרים
 */
const AgentProductsPage = () => {
  const navigate = useNavigate();

  // State
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [draftCounts, setDraftCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [bundlesLoading, setBundlesLoading] = useState(false);

  // Track if initial data has been loaded to prevent duplicate requests (using ref to avoid re-renders)
  const dataLoadedRef = useRef(false);

  // Tab State
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'bundles'

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState('brand_category_newest'); // Default: brand -> category -> newest
  const [sortOrder, setSortOrder] = useState('ASC'); // ASC or DESC
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyInStock, setShowOnlyInStock] = useState(false); // Filter: show only products with stock
  const [showOnlyHit, setShowOnlyHit] = useState(false); // Filter: show only featured/HIT products

  // Cart
  // isCartOpen removed - cart now navigates to separate page (LUM-15)

  // Bundle Modal
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [bundleQuantity, setBundleQuantity] = useState(1);

  // Image Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  // Cart management with auto-save
  const {
    cart,
    isLoading: cartLoading,
    isSaving,
    addItem,
    removeItem,
    updateQuantity,
    updatePrice,
    updateNotes,
    clearCart,
    getTotalItems,
    getTotalPrice
  } = useAgentDraftCart(selectedCompanyId);

  // Memoize fetch functions to prevent recreation on every render
  const fetchProducts = useCallback(async () => {
    try {
      console.log('Fetching products...');
      const response = await axios.get('/api/products', {
        params: { limit: 1000, status: 'active' }
      });
      const productsData = response.data.products || [];
      console.log(`Fetched ${productsData.length} products`);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('שגיאה בטעינת המוצרים');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchBrandsData = useCallback(async () => {
    try {
      const response = await axios.get('/api/brands', {
        params: { is_active: true, limit: 100, sort: 'name', order: 'asc' }
      });
      setBrandsList(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      // Silently fail - brands feature might not be available yet
      setBrandsList([]);
    }
  }, []);

  // Companies are now loaded by CompanySelector component itself (lazy loading)

  const fetchDraftCounts = useCallback(async () => {
    try {
      const response = await axios.get('/api/field-agent/draft-carts');
      const counts = {};
      (response.data.draftCarts || []).forEach(draft => {
        const itemCount = draft.cart_data?.items?.length || 0;
        counts[draft.company_id] = itemCount;
      });
      setDraftCounts(counts);
    } catch (error) {
      console.error('Error fetching draft counts:', error);
    }
  }, []);

  const fetchBundles = useCallback(async () => {
    try {
      setBundlesLoading(true);
      const response = await axios.get('/api/bundles', {
        params: {
          customer_type: 'b2b',
          is_active: true,
          limit: 100
        }
      });
      setBundles(response.data.bundles || []);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('שגיאה בטעינת החבילות');
    } finally {
      setBundlesLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data - Only once on mount (prevent re-fetching on hot-reload)
  useEffect(() => {
    // Prevent duplicate requests during the same session
    if (dataLoadedRef.current) {
      console.log('Data already loaded, skipping fetch');
      return;
    }

    // Mark as loading to prevent duplicate calls
    dataLoadedRef.current = true;

    console.log('Loading initial data...');

    // Load all initial data (companies loaded by CompanySelector)
    const loadData = async () => {
      try {
        await Promise.all([
          fetchProducts(),
          fetchCategories(),
          fetchBrandsData(),
          fetchDraftCounts(),
          fetchBundles()
        ]);
        console.log('Initial data loaded successfully');
      } catch (error) {
        // Don't reset flag - prevents infinite retry loop
        // User can manually refresh if needed
        console.error('Error loading initial data:', error);
        toast.error('שגיאה בטעינת הנתונים. אנא רענן את הדף');
        // Keep dataLoadedRef.current = true to prevent infinite loop
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Reset on unmount (for proper cleanup on navigation)
    return () => {
      // Reset on unmount so data can be reloaded if user navigates away and back
      dataLoadedRef.current = false;
    };
  }, [fetchProducts, fetchCategories, fetchBrandsData, fetchDraftCounts, fetchBundles]); // Memoized functions as deps

  // Update draft counts when cart changes - Optimized to prevent unnecessary re-renders
  useEffect(() => {
    if (selectedCompanyId && cart?.items) {
      const totalItems = cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      setDraftCounts(prev => {
        // Only update if value actually changed
        if (prev[selectedCompanyId] === totalItems) {
          return prev; // Return same reference to prevent re-render
        }
        return {
          ...prev,
          [selectedCompanyId]: totalItems
        };
      });
    }
  }, [cart?.items?.length, selectedCompanyId]); // Only depend on items length, not entire cart object

  // No longer needed - using brandsList from API instead
  // Get unique brands from products (legacy fallback)
  const uniqueBrands = useMemo(() => {
    if (brandsList.length > 0) return []; // Use API brands
    const brands = new Set();
    products.forEach(product => {
      if (product.brand) {
        brands.add(product.brand);
      }
    });
    return Array.from(brands).sort();
  }, [products, brandsList]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) {
      console.log('No products to filter');
      return [];
    }

    // Filter products
    let filtered = products.filter(product => {
      // Search in name, SKU, brand
      const searchLower = debouncedSearch.toLowerCase();
      const matchesSearch = !debouncedSearch ||
        product.name?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower) ||
        product.brand?.toLowerCase().includes(searchLower);

      // Category filter
      const matchesCategory = !selectedCategory ||
        product.category_id === selectedCategory;

      // Brand filter (supports both brand_id and legacy brand string)
      const matchesBrand = !selectedBrand ||
        product.brand_id === selectedBrand ||
        product.brand === selectedBrand ||
        product.brandInfo?.id === selectedBrand;

      // Stock filter
      const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0;
      const hasStock = stock >= 0; // Allow 0 stock for backorder products
      const matchesStockFilter = !showOnlyInStock || stock > 0; // If filter is on, only show products with stock > 0

      // HIT/Featured filter
      const matchesHitFilter = !showOnlyHit || product.is_featured === true;

      return matchesSearch && matchesCategory && matchesBrand && hasStock && matchesStockFilter && matchesHitFilter;
    });

    // Sort products
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'brand_category_newest':
          // Multi-level sort: Brand -> Category -> Created At (newest)
          // First: Sort by brand
          const aBrand = (a.brand || '').toLowerCase();
          const bBrand = (b.brand || '').toLowerCase();
          comparison = aBrand.localeCompare(bBrand, 'he');

          // If brands are equal, sort by category
          if (comparison === 0) {
            const aCatName = (categories.find(c => c.id === a.category_id)?.name || '').toLowerCase();
            const bCatName = (categories.find(c => c.id === b.category_id)?.name || '').toLowerCase();
            comparison = aCatName.localeCompare(bCatName, 'he');
          }

          // If categories are also equal, sort by created_at (newest first = DESC)
          if (comparison === 0) {
            const aDate = new Date(a.created_at || 0);
            const bDate = new Date(b.created_at || 0);
            comparison = bDate - aDate; // DESC for newest first
          }
          break;
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '', 'he');
          break;
        case 'brand':
          comparison = (a.brand || '').localeCompare(b.brand || '', 'he');
          break;
        case 'category':
          const aCatName = categories.find(c => c.id === a.category_id)?.name || '';
          const bCatName = categories.find(c => c.id === b.category_id)?.name || '';
          comparison = aCatName.localeCompare(bCatName, 'he');
          break;
        case 'created_at':
          const aDate = new Date(a.created_at || 0);
          const bDate = new Date(b.created_at || 0);
          comparison = aDate - bDate;
          break;
        case 'price':
          const aPrice = parseFloat(a.price_b2b || a.price_b2c || a.price || 0);
          const bPrice = parseFloat(b.price_b2b || b.price_b2c || b.price || 0);
          comparison = aPrice - bPrice;
          break;
        case 'stock':
          const aStock = a.stock_b2b || 0;
          const bStock = b.stock_b2b || 0;
          comparison = aStock - bStock;
          break;
        default:
          // Default: brand -> category -> newest
          const aDefaultBrand = (a.brand || '').toLowerCase();
          const bDefaultBrand = (b.brand || '').toLowerCase();
          comparison = aDefaultBrand.localeCompare(bDefaultBrand, 'he');

          if (comparison === 0) {
            const aDefaultCatName = (categories.find(c => c.id === a.category_id)?.name || '').toLowerCase();
            const bDefaultCatName = (categories.find(c => c.id === b.category_id)?.name || '').toLowerCase();
            comparison = aDefaultCatName.localeCompare(bDefaultCatName, 'he');
          }

          if (comparison === 0) {
            const aDefaultDate = new Date(a.created_at || 0);
            const bDefaultDate = new Date(b.created_at || 0);
            comparison = bDefaultDate - aDefaultDate; // DESC for newest first
          }
      }

      return sortOrder === 'ASC' ? comparison : -comparison;
    });

    console.log(`Filtered and sorted ${filtered.length} products from ${products.length} total`);
    return filtered;
  }, [products, debouncedSearch, selectedCategory, selectedBrand, sortBy, sortOrder, categories, showOnlyInStock, showOnlyHit]);

  // Memoize to prevent recalculations
  const getProductQuantityInCart = useCallback((productId) => {
    if (!cart?.items) return 0;
    const item = cart.items.find(i => i.product_id === productId);
    return item ? item.quantity : 0;
  }, [cart?.items]);

  const handleQuantityChange = (product, newQuantity) => {
    if (!selectedCompanyId) {
      toast.error('יש לבחור חברה קודם');
      return;
    }

    if (newQuantity <= 0) {
      removeItem(product.id);
    } else {
      // Check stock limit
      const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0;
      if (newQuantity > stock) {
        toast.error(`אין מספיק מלאי למוצר ${product.name}. נותר: ${stock}`);
        // If user tried to type a number larger than stock, we can either cap it or reject it.
        // Rejecting it (doing nothing) is safer than auto-capping which might go unnoticed.
        // But if item is already in cart, we might want to cap it?
        // Let's just reject the change for now.
        return;
      }

      const existingQty = getProductQuantityInCart(product.id);
      const b2bPrice = product.price_b2b || product.price_b2c || product.price;

      if (existingQty === 0) {
        addItem(product, newQuantity, b2bPrice);
      } else {
        updateQuantity(product.id, newQuantity);
      }
    }
  };

  // Wrapper for sidebar updateQuantity to check stock
  const handleSidebarUpdateQuantity = (productId, newQuantity, isTester, isBonus) => {
    // If it's a tester or bonus, we don't check stock (as per existing logic they don't affect stock or have 0 stock)
    if (isTester || isBonus) {
      updateQuantity(productId, newQuantity, isTester, isBonus);
      return;
    }

    // Find product to check stock
    const product = products.find(p => p.id === productId);
    if (product) {
      const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0;
      if (newQuantity > stock) {
        toast.error(`אין מספיק מלאי למוצר ${product.name}. נותר: ${stock}`);
        return;
      }
    }

    updateQuantity(productId, newQuantity, isTester, isBonus);
  };

  const handleAddTester = (product, event) => {
    // Prevent event bubbling - might interfere with other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!selectedCompanyId) {
      toast.error('יש לבחור חברה קודם');
      return;
    }

    // Tester items are always 0 price and don't affect stock
    // CRITICAL: Always add exactly 1 tester item per click, completely ignoring any quantity from product or cart
    // Force quantity to 1 explicitly, don't rely on default parameter
    // IMPORTANT: Don't use product.quantity or any other quantity - ALWAYS 1
    const forcedQuantity = 1;

    // Make sure product doesn't have a quantity property that might interfere
    const cleanProduct = { ...product };
    delete cleanProduct.quantity; // Remove quantity if it exists

    // EXTRA SAFETY: Log everything before calling addItem
    console.log('🔍 handleAddTester - BEFORE addItem:', {
      productId: product.id,
      productName: product.name,
      forcedQuantity: forcedQuantity,
      productHadQuantity: product.quantity,
      cleanProductHasQuantity: cleanProduct.quantity,
      qtyInCart: getProductQuantityInCart(product.id),
      testerQtyInCart: cart?.items?.filter(i => i.product_id === product.id && i.is_tester).reduce((sum, i) => sum + i.quantity, 0) || 0,
      allProductKeys: Object.keys(product),
      productObject: JSON.stringify(product, null, 2).substring(0, 500)
    });

    // Call addItem with EXPLICIT forcedQuantity = 1
    // CRITICAL: Pass forcedQuantity as second parameter, NOT from product or cart
    addItem(cleanProduct, forcedQuantity, 0, { is_tester: true });

    console.log('🔍 handleAddTester - AFTER addItem call');
  };

  const handleAddBonus = (product, event) => {
    // Prevent event bubbling - might interfere with other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!selectedCompanyId) {
      toast.error('יש לבחור חברה קודם');
      return;
    }
    // Bonus is always added as a new line item with price 0
    // CRITICAL: Always add exactly 1 bonus item per click, completely ignoring any quantity from product or cart
    // Force quantity to 1 explicitly, don't rely on default parameter
    // IMPORTANT: Don't use product.quantity or any other quantity - ALWAYS 1
    const forcedQuantity = 1;
    // Make sure product doesn't have a quantity property that might interfere
    const cleanProduct = { ...product };
    delete cleanProduct.quantity; // Remove quantity if it exists

    // EXTRA SAFETY: Log everything before calling addItem
    console.log('🔍 handleAddBonus - BEFORE addItem:', {
      productId: product.id,
      productName: product.name,
      forcedQuantity: forcedQuantity,
      productHadQuantity: product.quantity,
      cleanProductHasQuantity: cleanProduct.quantity,
      qtyInCart: getProductQuantityInCart(product.id),
      bonusQtyInCart: cart?.items?.filter(i => i.product_id === product.id && i.is_bonus).reduce((sum, i) => sum + i.quantity, 0) || 0,
      allProductKeys: Object.keys(product)
    });

    // Check stock for bonus item
    const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0;
    if (stock < forcedQuantity) {
      toast.error(`אין מספיק מלאי למוצר ${product.name} (בונוס). נותר: ${stock}`);
      return;
    }

    // Call addItem with EXPLICIT forcedQuantity = 1
    addItem(cleanProduct, forcedQuantity, 0, { is_bonus: true });

    console.log('🔍 handleAddBonus - AFTER addItem call');
  };

  const handleCreateOrder = async (checkoutData) => {
    if (!selectedCompanyId) {
      toast.error('יש לבחור חברה');
      return;
    }
    if (cart.items.length === 0) {
      toast.error('העגלה ריקה');
      return;
    }

    try {
      const orderData = {
        company_id: selectedCompanyId,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: parseFloat(item.price) || 0,
          notes: item.notes || '',
          is_tester: item.is_tester || false,
          is_bonus: item.is_bonus || false
        })),
        notes: checkoutData?.notes || '',
        payment_method: 'company_credit',
        status: 'pending',
        discount: checkoutData?.discount || 0,
        discountType: checkoutData?.discountType || 'fixed',
        discountValue: checkoutData?.discountValue || 0,
        discountReason: checkoutData?.discountReason || ''
      };

      const response = await axios.post('/api/field-agent/orders', orderData);

      toast.success('ההזמנה נוצרה בהצלחה!');
      await clearCart();
      navigate(`/agent/orders/${response.data.order.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.message || 'שגיאה ביצירת ההזמנה');
    }
  };

  // Selected company name is now managed by CompanySelector

  // Bundle handlers
  const handleViewBundleDetails = (bundle) => {
    setSelectedBundle(bundle);
    setShowBundleModal(true);
    setBundleQuantity(1);
  };

  const handleAddBundleToCart = async (bundle, quantity = 1) => {
    if (!selectedCompanyId) {
      toast.error('יש לבחור חברה קודם');
      return;
    }

    const bundleItems = bundle.bundle_items || [];

    // Pre-check stock for all items
    for (const item of bundleItems) {
      const product = item.product;
      const itemQuantity = item.quantity * quantity;
      // Check stock
      // Note: We need to ensure product object has stock info. 
      // If bundle.bundle_items includes full product info, we are good.
      // Usually it does.
      const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? product.stock_quantity ?? 0;

      if (itemQuantity > stock) {
        toast.error(`אין מספיק מלאי למוצר ${product.name} בחבילה. נותר: ${stock}, נדרש: ${itemQuantity}`);
        return;
      }
    }

    try {
      // Add each product in the bundle to cart
      for (const item of bundleItems) {
        const product = item.product;
        const itemQuantity = item.quantity * quantity; // Multiply by bundle quantity
        const b2bPrice = parseFloat(product.price_b2b || product.price_b2c || product.price || 0);

        await addItem(product, itemQuantity, b2bPrice);
      }

      toast.success(`חבילה "${bundle.title}" נוספה לעגלה!`);
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      toast.error('שגיאה בהוספת החבילה לעגלה');
    }
  };

  // Filter bundles
  const filteredBundles = useMemo(() => {
    return bundles.filter(bundle => {
      const searchLower = debouncedSearch.toLowerCase();
      const matchesSearch = !debouncedSearch ||
        bundle.title?.toLowerCase().includes(searchLower) ||
        bundle.description?.toLowerCase().includes(searchLower) ||
        bundle.bundle_sku?.toLowerCase().includes(searchLower);

      const matchesCategory = !selectedCategory ||
        bundle.category_id === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [bundles, debouncedSearch, selectedCategory]);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" dir="rtl">
      {/* Ultra Compact Sticky Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm" role="banner">
        <div className="px-2 sm:px-3 py-1.5">
          {/* Top Row: Title, Tabs, Results */}
          <div className="flex items-center gap-2 mb-1.5">
            {/* Title */}
            <h1 className="text-base font-bold text-gray-900 flex-shrink-0">
              {activeTab === 'products' ? 'מוצרים' : 'חבילות'}
            </h1>

            {/* Tab Switcher - Inline */}
            <div className="flex gap-1 bg-gray-100 p-0.5 rounded-md flex-shrink-0">
              <button
                onClick={() => setActiveTab('products')}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all flex items-center gap-1 ${activeTab === 'products'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-200'
                  }`}
                aria-label="מוצרים"
                aria-pressed={activeTab === 'products'}
              >
                <ShoppingCartIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">מוצרים</span>
              </button>
              <button
                onClick={() => setActiveTab('bundles')}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all flex items-center gap-1 ${activeTab === 'bundles'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-200'
                  }`}
                aria-label="חבילות"
                aria-pressed={activeTab === 'bundles'}
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">חבילות</span>
              </button>
            </div>

            {/* Results Count */}
            {!loading && !bundlesLoading && (
              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                {activeTab === 'products' ? filteredProducts.length : filteredBundles.length}
              </span>
            )}

            {/* In Stock Filter Toggle - Only for Products Tab */}
            {activeTab === 'products' && !loading && (
              <button
                onClick={() => setShowOnlyInStock(!showOnlyInStock)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all flex-shrink-0 ${showOnlyInStock
                  ? 'bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200'
                  : 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200'
                  }`}
                aria-label={showOnlyInStock ? 'הצג כל המוצרים' : 'הצג רק מוצרים במלאי'}
                title={showOnlyInStock ? 'לחץ להצגת כל המוצרים' : 'לחץ להצגת מוצרים זמינים במלאי'}
              >
                {showOnlyInStock ? (
                  <>
                    <XCircleIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">הצג כל המוצרים</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">הצג מוצרים במלאי</span>
                  </>
                )}
              </button>
            )}

            {/* HIT/Featured Filter Toggle */}
            {activeTab === 'products' && !loading && (
              <button
                onClick={() => setShowOnlyHit(!showOnlyHit)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all flex-shrink-0 ${showOnlyHit
                  ? 'bg-orange-200 text-orange-800 border border-orange-400 hover:bg-orange-300'
                  : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
                  }`}
                aria-label={showOnlyHit ? 'הצג כל המוצרים' : 'הצג רק מוצרי HIT'}
                title={showOnlyHit ? 'לחץ להצגת כל המוצרים' : 'לחץ להצגת מוצרי HIT בלבד'}
              >
                <span>HIT</span>
              </button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Filter Toggle - Top Right */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${showFilters
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              aria-label={showFilters ? 'הסתר סינון' : 'הצג סינון'}
            >
              <FunnelIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Second Row: Company & Search */}
          <div className="flex items-center gap-2">
            {/* Company Selector - Compact (with internal lazy loading) */}
            <div className="flex-1 min-w-0">
              <CompanySelector
                selectedCompanyId={selectedCompanyId}
                onCompanyChange={setSelectedCompanyId}
                draftCounts={draftCounts}
              />
            </div>

            {/* Search Bar - Compact */}
            <div className="flex-1 min-w-0 relative">
              <MagnifyingGlassIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חיפוש..."
                className="w-full pr-7 pl-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                aria-label="חיפוש מוצרים"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors"
                  aria-label="נקה חיפוש"
                >
                  <XMarkIcon className="h-3.5 w-3.5 text-gray-400" />
                </button>
              )}
            </div>

            {/* Clear Filters */}
            {(debouncedSearch || selectedCategory || selectedBrand || showOnlyInStock || showOnlyHit) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSelectedBrand('');
                  setShowOnlyInStock(false);
                  setShowOnlyHit(false);
                }}
                className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                aria-label="נקה סינון"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters Dropdown - Enhanced */}
          {showFilters && (
            <div className="mt-1.5 p-2 bg-gray-50 rounded-md border border-gray-200 space-y-2">
              {/* Category Filter */}
              <div>
                <label htmlFor="category-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  קטגוריה:
                </label>
                <select
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  aria-label="בחר קטגוריה"
                >
                  <option value="">כל הקטגוריות</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Brand Filter */}
              <div>
                <label htmlFor="brand-filter" className="block text-xs font-medium text-gray-700 mb-1">
                  מותג:
                </label>
                <select
                  id="brand-filter"
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  aria-label="בחר מותג"
                >
                  <option value="">כל המותגים</option>
                  {brandsList.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                  {uniqueBrands.map(brand => (
                    <option key={brand} value={brand}>
                      {brand} (ישן)
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label htmlFor="sort-by" className="block text-xs font-medium text-gray-700 mb-1">
                  מיין לפי:
                </label>
                <div className="flex gap-1">
                  <select
                    id="sort-by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    aria-label="בחר שדה למיון"
                  >
                    <option value="brand_category_newest">מותג → קטגוריה → החדש ביותר</option>
                    <option value="created_at">החדש ביותר</option>
                    <option value="name">שם מוצר</option>
                    <option value="brand">מותג</option>
                    <option value="category">קטגוריה</option>
                    <option value="price">מחיר</option>
                    <option value="stock">מלאי</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC')}
                    className="px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors flex items-center justify-center min-w-[40px]"
                    aria-label={`מיון ${sortOrder === 'ASC' ? 'עולה' : 'יורד'}`}
                    title={sortOrder === 'ASC' ? 'מיון עולה' : 'מיון יורד'}
                  >
                    {sortOrder === 'ASC' ? (
                      <ArrowUpIcon className="h-4 w-4 text-gray-600" aria-hidden="true" />
                    ) : (
                      <ArrowDownIcon className="h-4 w-4 text-gray-600" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              {/* Sort Order Indicator */}
              <div className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-300 rounded-md">
                {sortOrder === 'ASC' ? (
                  <>
                    <BarsArrowUpIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    <span className="text-xs font-medium text-gray-700">מיון עולה (א-ב, 1-9)</span>
                  </>
                ) : (
                  <>
                    <BarsArrowDownIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    <span className="text-xs font-medium text-gray-700">מיון יורד (ת-א, 9-1)</span>
                  </>
                )}
              </div>

              {/* Clear All Filters */}
              {(selectedCategory || selectedBrand || showOnlyInStock || showOnlyHit || sortBy !== 'brand_category_newest' || sortOrder !== 'ASC') && (
                <button
                  onClick={() => {
                    setSelectedCategory('');
                    setSelectedBrand('');
                    setShowOnlyInStock(false);
                    setShowOnlyHit(false);
                    setSortBy('brand_category_newest');
                    setSortOrder('ASC');
                  }}
                  className="w-full px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  aria-label="נקה כל הסינונים"
                >
                  נקה כל הסינונים
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content Area - Desktop Table & Mobile Cards */}
      <div className="px-2 sm:px-3 py-2 sm:py-3 pb-24">
        {activeTab === 'products' ? (
          // PRODUCTS VIEW
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden w-full">
              <div className="w-full overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-16 px-2 py-3 text-right text-xs font-semibold text-gray-700 uppercase">תמונה</th>
                      <th
                        className="px-2 py-3 text-right text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          if (sortBy === 'name') {
                            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                          } else {
                            setSortBy('name');
                            setSortOrder('ASC');
                          }
                        }}
                        title="לחץ למיון לפי שם"
                      >
                        <div className="flex items-center justify-end gap-1">
                          פרטי מוצר
                          {sortBy === 'name' && (
                            sortOrder === 'ASC' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-2 py-3 text-right text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                        onClick={() => {
                          if (sortBy === 'brand') {
                            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                          } else {
                            setSortBy('brand');
                            setSortOrder('ASC');
                          }
                        }}
                        title="לחץ למיון לפי מותג"
                      >
                        <div className="flex items-center justify-end gap-1">
                          מותג
                          {sortBy === 'brand' && (
                            sortOrder === 'ASC' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="w-14 px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          if (sortBy === 'stock') {
                            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                          } else {
                            setSortBy('stock');
                            setSortOrder('ASC');
                          }
                        }}
                        title="לחץ למיון לפי מלאי"
                      >
                        <div className="flex items-center justify-center gap-1">
                          מלאי
                          {sortBy === 'stock' && (
                            sortOrder === 'ASC' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                      <th
                        className="w-16 px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          if (sortBy === 'price') {
                            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                          } else {
                            setSortBy('price');
                            setSortOrder('ASC');
                          }
                        }}
                        title="לחץ למיון לפי מחיר"
                      >
                        <div className="flex items-center justify-center gap-1">
                          מחיר
                          {sortBy === 'price' && (
                            sortOrder === 'ASC' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                      <th className="w-32 px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">כמות</th>
                      <th className="w-20 px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">סה״כ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-12">
                          <LoadingSpinner size="lg" color="primary" text="טוען מוצרים..." />
                        </td>
                      </tr>
                    ) : filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-12">
                          <EmptyState
                            preset="search"
                            title="לא נמצאו מוצרים"
                            message="נסה לשנות את החיפוש או הסינון"
                          />
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((product, index) => {
                        const qtyInCart = getProductQuantityInCart(product.id);
                        const b2bPrice = parseFloat(product.price_b2b || product.price_b2c || product.price || 0);
                        const rowTotal = qtyInCart * b2bPrice;

                        let imgSrc = 'https://via.placeholder.com/50';
                        if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                          imgSrc = product.images[0];
                        } else if (product.main_image) {
                          imgSrc = product.main_image;
                        } else if (product.image_url) {
                          imgSrc = product.image_url;
                        }

                        return (
                          <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {/* Image */}
                            <td className="px-2 py-3 w-16">
                              <img
                                src={imgSrc}
                                alt={product.name}
                                className="h-12 w-12 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => {
                                  setLightboxImage(imgSrc);
                                  setLightboxAlt(product.name);
                                }}
                                onError={(e) => {
                                  if (!e.target.dataset.errorHandled) {
                                    e.target.dataset.errorHandled = 'true';
                                    e.target.src = 'https://via.placeholder.com/50?text=No+Image';
                                    e.target.onerror = null;
                                  }
                                }}
                                title="לחץ להגדלה"
                              />
                            </td>
                            {/* Product Details - Full name + SKU */}
                            <td className="px-2 py-3">
                              <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-sm font-medium text-gray-900 leading-tight">
                                    {product.name}
                                  </span>
                                  {product.is_featured && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200 whitespace-nowrap">
                                      HIT
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 font-mono">
                                  {product.sku || product.barcode || ''}
                                </span>
                              </div>
                            </td>
                            {/* Brand */}
                            <td className="px-2 py-3">
                              <span className="text-sm text-gray-700">
                                {product.brandInfo?.name || product.brand || '-'}
                              </span>
                            </td>
                            {/* Stock */}
                            <td className="px-2 py-3 text-sm text-gray-900 text-center w-14">
                              {(() => {
                                const stock = product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0;
                                return stock > 99 ? '99+' : stock;
                              })()}
                            </td>
                            {/* Price */}
                            <td className="px-2 py-3 text-sm text-gray-900 font-semibold text-center w-16">
                              ₪{b2bPrice.toFixed(0)}
                            </td>
                            {/* Quantity Controls + Bonus/Tester */}
                            <td className="px-2 py-3 w-32">
                              <div className="flex flex-col gap-1 items-center">
                                <div className="flex items-center gap-1 justify-center">
                                  <button
                                    onClick={() => handleQuantityChange(product, qtyInCart - 1)}
                                    disabled={qtyInCart === 0 || !selectedCompanyId}
                                    className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <MinusIcon className="h-4 w-4" />
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    value={qtyInCart}
                                    onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 0)}
                                    disabled={!selectedCompanyId}
                                    className="w-12 text-center border border-gray-300 rounded py-1.5 text-sm"
                                  />
                                  <button
                                    onClick={() => handleQuantityChange(product, qtyInCart + 1)}
                                    disabled={!selectedCompanyId}
                                    className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                {/* Bonus & Tester buttons - enlarged for touch */}
                                <div className="flex items-center gap-1.5 justify-center">
                                  <button
                                    onClick={(e) => handleAddTester(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="px-3 py-2 border-2 border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 active:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1 touch-manipulation min-h-[40px]"
                                    title="הוסף טסטר"
                                  >
                                    <BeakerIcon className="h-4 w-4" />
                                    <span>טסטר</span>
                                  </button>
                                  <button
                                    onClick={(e) => handleAddBonus(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="px-3 py-2 border-2 border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 active:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1 touch-manipulation min-h-[40px]"
                                    title="הוסף בונוס"
                                  >
                                    <GiftIcon className="h-4 w-4" />
                                    <span>בונוס</span>
                                  </button>
                                </div>
                              </div>
                            </td>
                            {/* Total */}
                            <td className="px-2 py-3 text-sm text-gray-900 font-bold text-center w-20">
                              {rowTotal > 0 ? `₪${rowTotal.toFixed(0)}` : '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards View - Enhanced */}
            <div className="lg:hidden space-y-2 px-2">
              {loading ? (
                <LoadingSpinner
                  size="xl"
                  color="primary"
                  text="טוען מוצרים..."
                  className="py-12"
                />
              ) : filteredProducts.length === 0 ? (
                <EmptyState
                  preset="search"
                  title="לא נמצאו מוצרים"
                  message="נסה לשנות את החיפוש או הסינון"
                />
              ) : (
                filteredProducts.map((product) => {
                  const qtyInCart = getProductQuantityInCart(product.id);
                  const b2bPrice = parseFloat(product.price_b2b || product.price_b2c || product.price || 0);
                  const rowTotal = qtyInCart * b2bPrice;

                  // Get image
                  let imgSrc = 'https://via.placeholder.com/80';
                  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                    imgSrc = product.images[0];
                  } else if (product.main_image) {
                    imgSrc = product.main_image;
                  } else if (product.image_url) {
                    imgSrc = product.image_url;
                  }

                  return (
                    <div
                      key={product.id}
                      className={`bg-white rounded-lg border transition-all duration-150 hover:shadow-md ${qtyInCart > 0
                        ? 'border-blue-400 shadow-md ring-1 ring-blue-100'
                        : 'border-gray-200 shadow-sm hover:border-gray-300'
                        }`}
                      role="article"
                      aria-label={`מוצר: ${product.name}`}
                    >
                      <div className="p-2">
                        <div className="flex gap-2">
                          {/* Product Image - Compact */}
                          <div className="flex-shrink-0">
                            <img
                              src={imgSrc}
                              alt={product.name || 'תמונת מוצר'}
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-75 transition-opacity active:scale-95"
                              loading="lazy"
                              onClick={() => {
                                setLightboxImage(imgSrc);
                                setLightboxAlt(product.name);
                              }}
                              onError={(e) => {
                                if (!e.target.dataset.errorHandled) {
                                  e.target.dataset.errorHandled = 'true';
                                  e.target.src = 'https://via.placeholder.com/80?text=No+Image';
                                  e.target.onerror = null; // Prevent infinite loop
                                }
                              }}
                              title="לחץ להגדלה"
                            />
                          </div>

                          {/* Product Info - Compact */}
                          <div className="flex-1 min-w-0">
                            {/* Name */}
                            <div className="flex items-start gap-1 mb-1">
                              <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 leading-tight">
                                {product.name}
                              </h3>
                              {product.is_featured && (
                                <span className="inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200">
                                  HIT
                                </span>
                              )}
                            </div>

                            {/* Meta Info - Compact */}
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500 mb-2">
                              {product.brand && (
                                <span className="text-gray-700 font-medium">מותג: {product.brand}</span>
                              )}
                              {product.sku && (
                                <span className="text-gray-600">מק״ט: {product.sku}</span>
                              )}
                              {product.category?.name && (
                                <span className="text-gray-500">• {product.category.name}</span>
                              )}
                            </div>

                            {/* Price & Stock Info */}
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className="text-base font-bold text-blue-600">
                                ₪{b2bPrice.toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500">
                                מלאי: {product.stock_b2b ?? product.stock_info?.stock_b2b ?? 0}
                              </span>
                              {qtyInCart > 0 && (
                                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                  סה״כ: ₪{rowTotal.toFixed(2)}
                                </span>
                              )}
                            </div>

                            {/* Quantity Controls - Full Width */}
                            {qtyInCart === 0 ? (
                              <div className="space-y-2">
                                <button
                                  onClick={() => handleQuantityChange(product, 1)}
                                  disabled={!selectedCompanyId}
                                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 touch-manipulation"
                                  aria-label={`הוסף ${product.name} לעגלה`}
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  <span>הוסף לעגלה</span>
                                </button>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => handleAddTester(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="flex-1 px-3 py-2.5 border-2 border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 active:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-1.5 touch-manipulation min-h-[44px]"
                                    aria-label="הוסף טסטר"
                                  >
                                    <BeakerIcon className="h-5 w-5" />
                                    <span>טסטר</span>
                                  </button>
                                  <button
                                    onClick={(e) => handleAddBonus(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="flex-1 px-3 py-2.5 border-2 border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 active:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-1.5 touch-manipulation min-h-[44px]"
                                    aria-label="הוסף בונוס"
                                  >
                                    <GiftIcon className="h-5 w-5" />
                                    <span>בונוס</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div className="flex items-center gap-1.5 w-full">
                                  <button
                                    onClick={() => handleQuantityChange(product, qtyInCart - 1)}
                                    className="flex-shrink-0 p-2 bg-red-50 border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-100 active:bg-red-200 touch-manipulation w-10 h-10 flex items-center justify-center shadow-sm"
                                    aria-label="הפחת כמות"
                                  >
                                    <MinusIcon className="h-4 w-4" />
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    value={qtyInCart}
                                    onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 0)}
                                    className="flex-1 min-w-0 text-center border-2 border-blue-300 rounded-lg py-1.5 text-sm font-bold focus:ring-2 focus:ring-blue-500 h-10"
                                    aria-label={`כמות ${product.name}`}
                                  />
                                  <button
                                    onClick={() => handleQuantityChange(product, qtyInCart + 1)}
                                    className="flex-shrink-0 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 touch-manipulation w-10 h-10 flex items-center justify-center shadow-sm"
                                    aria-label="הוסף כמות"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => handleAddTester(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="flex-1 px-3 py-2.5 border-2 border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 active:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-1.5 touch-manipulation min-h-[44px]"
                                    aria-label="הוסף טסטר"
                                  >
                                    <BeakerIcon className="h-5 w-5" />
                                    <span>טסטר</span>
                                  </button>
                                  <button
                                    onClick={(e) => handleAddBonus(product, e)}
                                    disabled={!selectedCompanyId}
                                    className="flex-1 px-3 py-2.5 border-2 border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 active:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-1.5 touch-manipulation min-h-[44px]"
                                    aria-label="הוסף בונוס"
                                  >
                                    <GiftIcon className="h-5 w-5" />
                                    <span>בונוס</span>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          // BUNDLES VIEW - Enhanced
          <>
            {bundlesLoading ? (
              <LoadingSpinner
                size="xl"
                color="primary"
                text="טוען חבילות..."
                className="py-12"
              />
            ) : filteredBundles.length === 0 ? (
              <EmptyState
                preset="generic"
                title="אין חבילות זמינות"
                message="חבילות חדשות יתווספו בקרוב"
                icon={SparklesIcon}
              />
            ) : (
              <div className="space-y-3 px-2">
                {filteredBundles.map((bundle) => (
                  <BundleCard
                    key={bundle.id}
                    bundle={bundle}
                    onViewDetails={handleViewBundleDetails}
                    onAddToCart={(b) => handleAddBundleToCart(b, 1)}
                    disabled={!selectedCompanyId}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bundle Detail Modal */}
      <BundleDetailModal
        isOpen={showBundleModal}
        onClose={() => {
          setShowBundleModal(false);
          setSelectedBundle(null);
          setBundleQuantity(1);
        }}
        bundle={selectedBundle}
        quantity={bundleQuantity}
        onQuantityChange={setBundleQuantity}
        onAddToCart={handleAddBundleToCart}
        disabled={!selectedCompanyId}
      />

      {/* Floating Cart Button - navigates to cart page */}
      {selectedCompanyId && cart.items.length > 0 && (
        <>
          {/* Mobile - Full Width Bottom */}
          <div className="lg:hidden fixed bottom-24 left-0 right-0 z-30 p-4 bg-gradient-to-t from-white via-white to-transparent backdrop-blur-sm">
            <AccessibleButton
              onClick={() => navigate(`/agent/cart/${selectedCompanyId}`)}
              variant="primary"
              fullWidth
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-2xl"
              ariaLabel={`פתח עגלה עם ${getTotalItems()} פריטים`}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <ShoppingCartIcon className="h-6 w-6" />
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">
                      {getTotalItems()} פריטים
                    </div>
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-xs opacity-90">
                    לחץ לסיום ההזמנה
                  </div>
                </div>
              </div>
            </AccessibleButton>
          </div>

          {/* Desktop - Compact Bottom Right */}
          <div className="hidden lg:block fixed bottom-6 right-72 z-30">
            <AccessibleButton
              onClick={() => navigate(`/agent/cart/${selectedCompanyId}`)}
              variant="primary"
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-xl hover:shadow-2xl"
              ariaLabel={`פתח עגלה עם ${getTotalItems()} פריטים`}
            >
              <div className="flex items-center gap-3">
                <ShoppingCartIcon className="h-5 w-5" />
                <div>
                  <div className="font-bold">
                    {getTotalItems()} פריטים
                  </div>
                </div>
              </div>
            </AccessibleButton>
          </div>
        </>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        src={lightboxImage}
        alt={lightboxAlt}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

export default AgentProductsPage;
